import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ n, label, tone, testid }) => (
  <div data-testid={testid} className={`rounded-lg border px-3 py-2 ${tone}`}>
    <div className="font-display text-2xl font-bold leading-none">{n}</div>
    <div className="text-xs mt-1">{label}</div>
  </div>
);

const NameList = ({ title, items, extra }) =>
  items?.length ? (
    <details className="text-sm">
      <summary className="cursor-pointer font-medium text-slate-700">{title}</summary>
      <ul className="mt-1 max-h-32 overflow-auto pl-5 list-disc text-slate-600">
        {items.map((x, i) => <li key={i}>{typeof x === "string" ? x : `${x.name} — ${x.fields.join(", ")}`}</li>)}
        {extra > items.length && <li className="list-none text-slate-400">… en {extra - items.length} meer</li>}
      </ul>
    </details>
  ) : null;

const KINDS = {
  excel: {
    url: "/leads/import", accept: ".xlsx", title: "Excel uploaden", pick: "Kies een .xlsx-bestand…",
    desc: <>Upload een bijgewerkte export (met kolom "ID (niet wijzigen)"). Je ziet eerst wat er verandert;
      pas na <b>Doorvoeren</b> wordt er opgeslagen. CRM-status en verzendhistorie blijven altijd ongemoeid.</>,
  },
  map: {
    url: "/leads/import-map", accept: ".kmz,.kml", title: "Kaart uploaden (KMZ/KML)", pick: "Kies een .kmz- of .kml-bestand…",
    desc: <>Upload een kaart uit Google My Maps of Google Earth. Per punt worden naam, adres, website, e-mail en
      telefoon uitgelezen en als <b>nieuwe leads</b> toegevoegd aan dit profiel. Bestaande leads worden overgeslagen.</>,
  },
};

export const LeadsImport = ({ open, onOpenChange, profile, onDone, onFindEmails, kind = "excel" }) => {
  const K = KINDS[kind];
  const [noEmailIds, setNoEmailIds] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setPreview(null); if (inputRef.current) inputRef.current.value = ""; };
  const send = (f, apply) => {
    const fd = new FormData();
    fd.append("file", f);
    return axios.post(`${API}${K.url}`, fd, { params: { profile, apply } });
  };
  const pick = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setBusy(true);
    try { setPreview((await send(f, false)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Kon het bestand niet lezen"); reset(); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    if (kind === "excel" && preview.deleted > 0 && !window.confirm(`Let op: ${preview.deleted} lead(s) worden verwijderd omdat ze niet meer in je Excel staan.\n\nEr wordt eerst een herstelpunt gemaakt. Doorgaan?`)) return;
    setBusy(true);
    try {
      const { data } = await send(file, true);
      toast.success(kind === "map"
        ? `${data.added} nieuwe lead(s) uit de kaart toegevoegd (${data.with_email} met e-mail). Herstelpunt opgeslagen.`
        : `Doorgevoerd: ${data.updated} bijgewerkt, ${data.added} nieuw, ${data.deleted} verwijderd. Herstelpunt opgeslagen.`);
      reset(); onDone?.();
      if (data.new_without_email?.length && onFindEmails) setNoEmailIds(data.new_without_email);
      else onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Doorvoeren mislukt"); }
    finally { setBusy(false); }
  };
  const nothing = preview && !preview.updated && !preview.added && !preview.deleted;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); setNoEmailIds([]); } onOpenChange(v); }}>
      <DialogContent data-testid={kind === "map" ? "map-import-dialog" : "leads-import-dialog"} className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">{K.title}</DialogTitle>
          <DialogDescription>{K.desc}</DialogDescription>
        </DialogHeader>

        <label className="flex items-center gap-3 rounded-lg border-2 border-dashed border-slate-300 p-4 cursor-pointer hover:border-[#FF4500] transition-colors">
          {busy ? <Loader2 className="w-5 h-5 animate-spin text-[#FF4500]" /> : <FileSpreadsheet className="w-5 h-5 text-[#FF4500]" />}
          <span className="text-sm text-slate-700">{file ? file.name : K.pick}</span>
          <input ref={inputRef} data-testid={kind === "map" ? "map-import-file" : "leads-import-file"} type="file" accept={K.accept} className="hidden"
            onChange={(e) => pick(e.target.files?.[0])} />
        </label>

        {noEmailIds.length > 0 && (
          <div data-testid="find-emails-box" className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 space-y-2">
            <p>Opgeslagen. <b>{noEmailIds.length}</b> nieuwe lead(s) hebben een website maar nog geen e-mailadres.</p>
            <Button data-testid="find-emails-button" size="sm" onClick={() => { onFindEmails(noEmailIds); setNoEmailIds([]); onOpenChange(false); }}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
              Zoek e-mails voor de {noEmailIds.length} nieuwe leads
            </Button>
          </div>
        )}

        {preview && (
          <div data-testid="leads-import-preview" className="space-y-3">
            {kind === "excel" && !preview.has_id_column && (
              <p className="flex gap-2 text-sm text-amber-700 bg-amber-50 rounded-md p-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> Geen ID-kolom gevonden: alle rijen worden als nieuw behandeld en er wordt niets verwijderd.
              </p>
            )}
            {kind === "map" ? (
              <div className="grid grid-cols-4 gap-2">
                <Stat testid="preview-points" n={preview.points} label="punten op kaart" tone="border-slate-200 bg-white text-slate-600" />
                <Stat testid="preview-added" n={preview.added} label="nieuw" tone="border-green-200 bg-green-50 text-green-800" />
                <Stat testid="preview-with-email" n={preview.with_email} label="nieuw mét e-mail" tone="border-blue-200 bg-blue-50 text-blue-800" />
                <Stat testid="preview-duplicates" n={preview.duplicates} label="bestaat al, overgeslagen" tone="border-slate-200 bg-slate-50 text-slate-600" />
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
              <Stat testid="preview-updated" n={preview.updated} label="bijgewerkt" tone="border-blue-200 bg-blue-50 text-blue-800" />
              <Stat testid="preview-added" n={preview.added} label="nieuw" tone="border-green-200 bg-green-50 text-green-800" />
              <Stat testid="preview-deleted" n={preview.deleted} label="verwijderd" tone="border-red-200 bg-red-50 text-red-800" />
              <Stat testid="preview-duplicates" n={preview.duplicates} label="dubbel, overgeslagen" tone="border-slate-200 bg-slate-50 text-slate-600" />
              <Stat testid="preview-unchanged" n={preview.unchanged} label="ongewijzigd" tone="border-slate-200 bg-white text-slate-500" />
            </div>
            )}
            <NameList title="Bijgewerkt" items={preview.update_list} extra={preview.updated} />
            <NameList title="Nieuw" items={preview.add_list} extra={preview.added} />
            <NameList title="Wordt verwijderd" items={preview.delete_list} extra={preview.deleted} />
            <NameList title="Overgeslagen (bestaat al)" items={preview.duplicate_list} extra={preview.duplicates} />
            {preview.deleted > 0 && preview.export_ref && (
              <p className="text-xs text-slate-500">Verwijderd = stond in je laatste export ({new Date(preview.export_ref).toLocaleString("nl-NL")}) maar niet meer in deze upload.</p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={reset} className="rounded-full">Annuleren</Button>
              <Button data-testid="leads-import-apply" disabled={busy || nothing} onClick={apply}
                className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                {nothing ? "Geen wijzigingen" : "Doorvoeren"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
